/**
 * FR-AUD-002 — Best-effort stable device identifier for the audit trail.
 *
 * Strategy: SHA-256 of (userAgent + screen dims + timezone + language). Cached
 * in localStorage under `mt-device-id` so the same value is reused across
 * sessions even when the user logs out. Falls back to a random UUID if the
 * Web Crypto API is unavailable.
 *
 * Not a security boundary. Stronger fingerprinting (FingerprintJS) can replace
 * this without touching the consumer (the axios interceptor reads
 * `getDeviceId()` once at module init).
 */

const STORAGE_KEY = 'mt-device-id';

let cached: string | null = null;
let pendingInit: Promise<string> | null = null;

async function computeFingerprint(): Promise<string> {
  if (typeof window === 'undefined') return randomFallback();

  const parts = [
    navigator.userAgent,
    String(screen.width),
    String(screen.height),
    String(screen.colorDepth),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    String(navigator.hardwareConcurrency),
  ];
  const seed = parts.join('|');

  try {
    const encoder = new TextEncoder();
    const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(seed));
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return randomFallback();
  }
}

function randomFallback(): string {
  try {
    return crypto.randomUUID().replace(/-/g, '');
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

/**
 * Returns the cached device id synchronously if available, otherwise kicks off
 * computation. Use {@link initDeviceId} on app startup to ensure the id is
 * ready before the first request.
 */
export function getDeviceId(): string | null {
  if (cached) return cached;
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) {
    cached = stored;
    return stored;
  }
  return null;
}

/**
 * Compute (or load) the device id and persist it. Safe to call multiple times
 * — subsequent calls return the same promise.
 */
export function initDeviceId(): Promise<string> {
  if (cached) return Promise.resolve(cached);
  if (pendingInit) return pendingInit;
  if (typeof window === 'undefined') return Promise.resolve(randomFallback());

  pendingInit = (async () => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
    const computed = await computeFingerprint();
    try {
      window.localStorage.setItem(STORAGE_KEY, computed);
    } catch {
      // localStorage disabled (private mode); keep in-memory.
    }
    cached = computed;
    return computed;
  })();

  return pendingInit;
}
